import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
} from "react";
import { AuthContext } from "../auth-context";
import { useAsyncFn } from "../../hooks/use-async-fn";

export interface EventTypeRecord {
  event_type: string;
  total_count: number;
  query_count: number;
  row_count: number;
}

export interface EventMonthRecord extends EventTypeRecord {
  month: number;
  month_name: string;
  year: number;
}

export interface FederationMonthRecord extends EventMonthRecord {
  federation_id: string;
  federation_name: string;
}

export interface OrganizationMonthRecord extends EventMonthRecord {
  organization_id: string;
  organization_name: string;
}

export interface AdserverMonthRecord extends EventMonthRecord {
  destination_adserver_type: string;
}

export interface OrgActivationEnrichmentMonthRecord extends EventMonthRecord {
  organization_id: string;
  organization_name: string;
  activation_enrichment: "activation" | "enrichment";
}

export interface ActiveFederationRecord {
  month: number;
  month_name: string;
  year: number;
  active_federation_count: number;
  federation_ids: string[];
}

export interface DashboardData {
  eventType: EventTypeRecord[];
  eventMonth: EventMonthRecord[];
  federationMonth: FederationMonthRecord[];
  organisationMonth: OrganizationMonthRecord[];
  adserverMonth: AdserverMonthRecord[];
  orgActivationEnrichmentMonth: OrgActivationEnrichmentMonthRecord[];
  activeFederations: ActiveFederationRecord[];
  uniqueActiveFederationCount: number | null;
}

export interface DashboardMeta {
  start_date: string;
  end_date: string;
  keys: string[];
}

export interface DashboardResponse {
  data: DashboardData;
  meta: DashboardMeta;
}

interface DashboardContextType {
  dashboardData: DashboardData | null;
  meta: DashboardMeta | null;
  isLoading: boolean;
  isFulfilled: boolean;
  error: string | null;
  dataErrors: Partial<Record<keyof DashboardData, boolean>>;
  lastRefreshedAt: Date | null;
  refetch: (startDate: string, endDate: string, applicationIds?: string[] | null, federationIds?: string[] | null, organizationIds?: string[] | null, keys?: string[]) => void;
}

export const DashboardContext = createContext<DashboardContextType | undefined>(
  undefined,
);

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
  const { getDashboard } = useContext(AuthContext);
  const getDashboardFn = useAsyncFn({ deferFn: getDashboard });
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);

  useEffect(() => {
    const now = new Date();
    const fmtLocal = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    };
    const start = fmtLocal(
      new Date(now.getFullYear(), now.getMonth() - 3, now.getDate()),
    );
    const end = fmtLocal(now);
    getDashboardFn.run({ start_date: start, end_date: end });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Capture timestamp whenever data successfully arrives
  useEffect(() => {
    if (getDashboardFn.isFulfilled) {
      setLastRefreshedAt(new Date());
    }
  }, [getDashboardFn.isFulfilled, getDashboardFn.data]);

  const response: DashboardResponse | undefined = getDashboardFn.data;
  const raw = response?.data;
  const dataErrors: Partial<Record<keyof DashboardData, boolean>> = {};
  const safeArr = (val: any, key: keyof DashboardData): any[] => {
    if (Array.isArray(val)) return val;
    // null/undefined means no data → empty state; only flag structurally wrong types as errors
    if (val !== null && val !== undefined) dataErrors[key] = true;
    return [];
  };
  const dashboardData: DashboardData | null = raw
    ? {
        eventType: safeArr(raw.eventType, "eventType"),
        eventMonth: safeArr(raw.eventMonth, "eventMonth"),
        federationMonth: safeArr(raw.federationMonth, "federationMonth"),
        organisationMonth: safeArr(raw.organisationMonth, "organisationMonth"),
        adserverMonth: safeArr(raw.adserverMonth, "adserverMonth"),
        orgActivationEnrichmentMonth: safeArr(
          raw.orgActivationEnrichmentMonth,
          "orgActivationEnrichmentMonth",
        ),
        activeFederations: safeArr(raw.activeFederations, "activeFederations"),
        uniqueActiveFederationCount:
          (raw.activeFederations as any)?.summary
            ?.unique_active_federation_count ?? null,
      }
    : null;

  return (
    <DashboardContext.Provider
      value={{
        dashboardData,
        meta: response?.meta ?? null,
        isLoading: getDashboardFn.isPending,
        isFulfilled: getDashboardFn.isFulfilled,
        error: getDashboardFn.isRejected ? String(getDashboardFn.error) : null,
        dataErrors,
        lastRefreshedAt,
        refetch: (startDate, endDate, applicationIds, federationIds, organizationIds, keys) => {
          getDashboardFn.run({
            start_date: startDate,
            end_date: endDate,
            ...(federationIds?.length ? { federation_ids: federationIds } : {}),
            ...(organizationIds?.length ? { organization_ids: organizationIds } : {}),
            keys,
          });
        },
      }}
    >
      {children}
    </DashboardContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useDashboardContext = (): DashboardContextType => {
  const ctx = useContext(DashboardContext);
  if (!ctx)
    throw new Error(
      "useDashboardContext must be used within a DashboardProvider",
    );
  return ctx;
};
